/* IndexedDB-backed offline submission queue for AMPL Plant Manager.
 *
 * Used by high-traffic operator forms (Stage-2 strokes + Machine Checksheet)
 * so a brief WiFi drop doesn't block submissions. The form is "submitted"
 * locally and replayed automatically when connectivity is restored.
 */

const DB_NAME = "ampl_offline";
const STORE = "queue";
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) return reject(new Error("IndexedDB not available"));
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueue(item) {
  const record = {
    ...item,
    queued_at: new Date().toISOString(),
    attempts: 0,
  };
  return tx("readwrite", (s) => s.add(record));
}

export async function listQueue() {
  return tx("readonly", (s) => {
    return new Promise((resolve, reject) => {
      const req = s.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function removeItem(id) {
  return tx("readwrite", (s) => s.delete(id));
}

export async function queueLength() {
  const items = await listQueue();
  return items.length;
}

function isNetworkError(err) {
  // No response from server → network/offline. 4xx/5xx = server reachable, real failure.
  return !err?.response;
}

/* Submit immediately if online; queue for later if offline or the request
 * fails because the network is unavailable. Returns:
 *   { online: true,  data }      → the server's real response
 *   { online: false, queued: true, label } → enqueued, will retry later
 * Real server-side errors (4xx/5xx) are re-thrown so the form can show them.
 */
export async function submitOnlineOrQueue(api, { method = "post", url, payload, label }) {
  const tryNetwork = navigator.onLine;
  if (tryNetwork) {
    try {
      const res = await api[method](url, payload);
      return { online: true, data: res.data };
    } catch (err) {
      if (!isNetworkError(err)) throw err; // genuine 4xx/5xx, surface to UI
      // fall through to queue
    }
  }
  await enqueue({ method, url, payload, label });
  return { online: false, queued: true, label };
}

export async function flushQueue(api, { onProgress } = {}) {
  if (!navigator.onLine) return { sent: 0, remaining: await queueLength() };
  const items = await listQueue();
  let sent = 0;
  for (const item of items) {
    try {
      const m = item.method || "post";
      await api[m](item.url, item.payload);
      await removeItem(item.id);
      sent += 1;
      onProgress?.(sent, items.length);
    } catch (err) {
      if (isNetworkError(err)) break; // still offline; stop and retry later
      // 4xx/5xx → drop the bad item (otherwise it blocks the queue forever)
      console.warn("offlineQueue: dropping non-retryable item", item, err?.response?.data);
      await removeItem(item.id);
    }
  }
  return { sent, remaining: await queueLength() };
}
